import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, ShieldCheck, QrCode, ShieldOff } from "lucide-react";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user } = useAuth();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const [totpStatus, setTotpStatus] = useState({ enabled: false, has_secret: false });
  const [totpSecret, setTotpSecret] = useState("");
  const [totpQr, setTotpQr] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpBusy, setTotpBusy] = useState(false);

  const loadTotp = async () => {
    try {
      const { data } = await api.get("/auth/2fa/status");
      setTotpStatus(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    loadTotp();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) {
      toast.error("Le nouveau mot de passe doit faire au moins 6 caractères.");
      return;
    }
    if (newPwd !== confirm) {
      toast.error("La confirmation ne correspond pas.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success("Mot de passe modifié.");
      setOldPwd("");
      setNewPwd("");
      setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const generateSecret = async () => {
    setTotpBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/generate");
      setTotpSecret(data.secret);
      setTotpQr(data.qr_png_base64);
      await loadTotp();
      toast.success("Secret généré. Scannez le QR-code puis confirmez avec un code.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTotpBusy(false);
    }
  };

  const enableTotp = async (e) => {
    e?.preventDefault();
    setTotpBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code: totpCode });
      setTotpCode("");
      setTotpSecret("");
      setTotpQr("");
      await loadTotp();
      toast.success("2FA activée. Désormais requise à chaque connexion.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTotpBusy(false);
    }
  };

  const disableTotp = async () => {
    if (!window.confirm("Désactiver la 2FA ? Vous perdrez cette couche de sécurité.")) return;
    setTotpBusy(true);
    try {
      await api.post("/auth/2fa/disable");
      setTotpSecret("");
      setTotpQr("");
      await loadTotp();
      toast.success("2FA désactivée.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTotpBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-xl" data-testid="profile-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
          // Sécurité du compte
        </p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Mon profil</h1>
        {user && (
          <p className="text-muted-foreground mt-2 text-sm">
            Connecté en tant que <strong>{user.email}</strong>
          </p>
        )}
      </div>

      <form onSubmit={submit} className="border border-border bg-card rounded-sm p-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="h-4 w-4 text-primary" />
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Changer le mot de passe</h2>
        </div>

        <div className="space-y-2">
          <Label htmlFor="old_password">Mot de passe actuel</Label>
          <Input
            id="old_password"
            type="password"
            value={oldPwd}
            onChange={(e) => setOldPwd(e.target.value)}
            required
            autoComplete="current-password"
            data-testid="profile-old-password"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new_password">Nouveau mot de passe (min. 6 caractères)</Label>
          <Input
            id="new_password"
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            data-testid="profile-new-password"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm">Confirmer le nouveau mot de passe</Label>
          <Input
            id="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            data-testid="profile-confirm-password"
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} data-testid="profile-save-btn">
            <ShieldCheck className="h-4 w-4 mr-2" /> {saving ? "Modification…" : "Modifier le mot de passe"}
          </Button>
        </div>
      </form>

      {/* 2FA TOTP */}
      <div className="border border-border bg-card rounded-sm p-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <QrCode className="h-4 w-4 text-primary" />
          <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Authentification à deux facteurs (2FA)</h2>
        </div>

        {totpStatus.enabled ? (
          <>
            <p className="text-sm">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-green-500/10 text-green-700 dark:text-green-400 font-medium">
                <ShieldCheck className="h-3.5 w-3.5" /> Activée
              </span>
              <span className="ml-2 text-muted-foreground">
                Un code à 6 chiffres vous est demandé à chaque connexion.
              </span>
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={disableTotp}
              disabled={totpBusy}
              data-testid="profile-totp-disable"
            >
              <ShieldOff className="h-4 w-4 mr-2" /> Désactiver la 2FA
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Protégez votre compte avec une appli comme <strong>Google Authenticator</strong>,{" "}
              <strong>Authy</strong>, <strong>1Password</strong> ou <strong>Microsoft Authenticator</strong>.
            </p>
            {!totpQr ? (
              <Button
                type="button"
                onClick={generateSecret}
                disabled={totpBusy}
                data-testid="profile-totp-generate"
              >
                <QrCode className="h-4 w-4 mr-2" /> Générer un secret
              </Button>
            ) : (
              <form onSubmit={enableTotp} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <img
                    src={`data:image/png;base64,${totpQr}`}
                    alt="QR code 2FA"
                    className="w-44 h-44 border border-border rounded bg-white p-1"
                    data-testid="profile-totp-qr"
                  />
                  <div className="flex-1 space-y-2 text-sm">
                    <p className="text-muted-foreground">
                      1. Scannez ce QR-code avec votre app d&apos;authentification.<br />
                      2. Ou saisissez ce secret manuellement :
                    </p>
                    <code className="block text-xs bg-muted p-2 rounded font-mono break-all">{totpSecret}</code>
                    <p className="text-muted-foreground">
                      3. Saisissez le code à 6 chiffres affiché pour valider :
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="totp_code">Code 2FA</Label>
                  <Input
                    id="totp_code"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123 456"
                    className="font-mono tracking-widest text-center text-lg max-w-[200px]"
                    data-testid="profile-totp-code"
                  />
                </div>
                <Button type="submit" disabled={totpBusy} data-testid="profile-totp-enable">
                  <ShieldCheck className="h-4 w-4 mr-2" /> Activer la 2FA
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
